import { createContext, useContext, type ReactNode } from "react";
import type { AccountEvent, FundedEvent, PayoutEvent, DayViolationSummary } from "../TradingCalendar";
import type { AccountFilterValue } from "../../trading-accounts/AccountGroupFilter";
import type { DateRange } from "../../client/components/DateRangePicker";
import type { CalendarEvent } from "../../prop-firm-manager/PropFirmCalendar";
import type {
  SymbolStats,
  MonthlyStats,
  RollingWinRate,
  HeatmapCell,
  TimeSlotStats,
  HoldDurationPoint,
  TagStats,
  BehavioralBiases,
  SessionStats,
  PipStats,
  PipBucket,
  PipSymbolStat,
  SizingPoint,
  LongShortStats,
  DayOfWeekStats,
  StreakData,
  ConsistencyScore,
  NetDailyPnlPoint,
} from "../../trading-journal/analytics";

export interface DashboardData {
  // — trading data —
  trades: any[];
  routines: any[];
  goals: any[];
  accounts: any[];
  allAccounts?: any[];
  groups: any[];
  strategies: any[];
  sessions: any[];
  metrics: any;
  statsTrades: any[];
  statsTradesList: any[];
  equityCurveData: { points: any[]; totalPnl: number; xMin: number; xMax: number };
  accountEvents: AccountEvent[];
  fundedEvents: FundedEvent[];
  payoutEvents: PayoutEvent[];
  violationsByDate: Map<string, DayViolationSummary>;
  pendingRoutines: any[];
  topGoals: any[];
  statsPeriod: "7d" | "30d" | "90d" | "all";
  accountFilter: AccountFilterValue;
  setAccountFilter: (f: AccountFilterValue) => void;
  strategyIds: string[];
  setStrategyIds: (ids: string[]) => void;
  statsStartDate: Date | null;
  statsEndDate: Date | null;
  setSelectedDay: (d: Date | null) => void;
  handleCompleteRoutine: (r: any) => void;
  setSelectedMonth: (d: Date) => void;
  onAddTrade: () => void;
  onPromoteToFunded: (account: any) => void;
  onOpenPlanModal: () => void;
  isDark: boolean;
  /** Active theme id (dark/light/jungle/sunrise/tokyo-night). Charts key off
   *  this so they re-read CSS-variable colors on ANY theme switch. */
  themeId: string;
  isFreePlan: boolean;
  readOnly: boolean;
  hasMentor: boolean;
  t: any;
  chartMode: "pnl" | "networth";
  setChartMode: (m: "pnl" | "networth") => void;
  previewChartHeight?: number;
  analyticsData?: {
    symbolStats: SymbolStats[];
    monthlyStats: MonthlyStats[];
    cumulativePnl: Array<{ x: number; y: number }>;
    pnlDistribution: Array<{ range: string; count: number }>;
    rollingWinRate: RollingWinRate[];
    heatmapData: HeatmapCell[];
    timeSlotStats: TimeSlotStats[];
    holdDurationData: HoldDurationPoint[];
    tagStats: TagStats[];
    behavioralBiases: BehavioralBiases;
    sessionStats: SessionStats;
    pipStats: PipStats;
    pipBuckets: PipBucket[];
    pipBySymbol: PipSymbolStat[];
    calmarRatio: number | null;
    recoveryFactor: number | null;
    sizingData: SizingPoint[];
    longShort: LongShortStats;
    dayOfWeek: DayOfWeekStats;
    streakData: StreakData | null;
    consistencyScore: ConsistencyScore | null;
    netDailyPnl: NetDailyPnlPoint[];
    drawdownCurve: Array<{ x: number; y: number }>;
  };

  // — PFM data (absorbed from PfmDataContext) —
  charges: any[];
  tptWalletContext: any;
  filteredAccounts: any[];
  calendarEvents: CalendarEvent[];
  pfmTemplateFilter: string | null;
  setPfmTemplateFilter: (id: string | null) => void;
  accountStatusFilter: string | null;
  setAccountStatusFilter: (status: string | null) => void;
  dateRange: DateRange;
  setDateRange: (r: DateRange) => void;
  calendarMonth: Date;
  setCalendarMonth: (d: Date) => void;
  showImportModal: boolean;
  setShowImportModal: (v: boolean) => void;
  showAddPayoutModal: boolean;
  setShowAddPayoutModal: (v: boolean) => void;
  isLoadingPfm: boolean;
  userId: string | undefined;
  hiddenAccountsCount: number;
  showPlanModal: boolean;
  setShowPlanModal: (v: boolean) => void;

  /** When the Discipline page is opened via /discipline-manager/note/:id, the
   *  notebook-browser widget pre-selects this note. */
  notebookInitialNoteId?: string;

  // — Journal page (scope "journal") —
  /** Filtre symbole (texte libre). */
  journalSymbol?: string;
  setJournalSymbol?: (v: string) => void;
  /** Filtre side : "all" | "long" | "short". */
  journalSide?: "all" | "long" | "short";
  setJournalSide?: (v: "all" | "long" | "short") => void;
  /** Filtre statut : "all" | "open" | "closed". */
  journalStatus?: "all" | "open" | "closed";
  setJournalStatus?: (v: "all" | "open" | "closed") => void;
  /** Filtre PnL min/max (strings vides = non défini). */
  journalMinPnl?: string;
  journalMaxPnl?: string;
  setJournalMinPnl?: (v: string) => void;
  setJournalMaxPnl?: (v: string) => void;
  /** Filtre tags (ids). */
  journalTagIds?: string[];
  setJournalTagIds?: (ids: string[]) => void;
  /** Liste des tags disponibles (pour le multi-select). */
  journalTags?: any[];
  /** Filtres avancés bruts (dialog) + setter — incluent customFields. */
  journalAdvancedFilters?: any;
  setJournalAdvancedFilters?: (f: any) => void;
  /** Données pour le dialog colonnes / filtres avancés. */
  journalCustomFields?: any[];
  journalColumnPreferences?: any[];
  journalTagSuggestions?: any[];
  /** Callbacks mutatifs déclenchés par le widget. */
  onEditTrade?: (trade: any) => void;
  onDeleteTrade?: (trade: any) => void;
  onBulkDeleteTrades?: (tradeIds: string[]) => void;
  onColumnsChanged?: () => void;
  onJournalTradesChanged?: () => void;
}

const WidgetDataCtx = createContext<DashboardData | null>(null);

export function WidgetDataProvider({ value, children }: { value: DashboardData; children: ReactNode }) {
  return <WidgetDataCtx.Provider value={value}>{children}</WidgetDataCtx.Provider>;
}

export function useWidgetData(): DashboardData {
  const ctx = useContext(WidgetDataCtx);
  if (!ctx) throw new Error("useWidgetData must be used within a WidgetDataProvider");
  return ctx;
}

// Backward-compat aliases
export const DashboardDataProvider = WidgetDataProvider;
export const useDashboardData = useWidgetData;
